// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    VSCodeUrl,
    assert,
    compareLocale,
    isNonEmptyString,
    models,
    ProfileTheme,
    getProfileFileName,
    isAbsoluteUrl,
} from '@microsoft/vscodeedu-api';
import { produce } from 'immer';
import React, { Dispatch, createContext, useCallback, useContext, useEffect, useReducer } from 'react';
import { injectIntl } from 'react-intl';
import { createSelector } from 'reselect';
import useAsyncEffect from 'use-async-effect';
import { ContextProps, StoreState, UserContext, anonymousClient, apiEnvironment, getUserId, useConfig } from '.';
import { Course } from '../models';
import { getContentUrl } from '../utilities';
import { useAsyncRunner } from '../utilities/async-runner';
import { traceEvent, trackError, trackTrace } from '../utilities/diagnostics';
import { useTheme } from './theme-context';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

// Course store.
export type CourseStore = Readonly<{
    state: StoreState;
    items: Course[];
    defaultCourseIds: string[];
    item?: Course;
}>;

// Course store action.
export type CourseAction =
    | { type: 'LoadResult'; items: Course[] }
    | { type: 'LoadError' }
    | { type: 'LoadUnitsResult'; courseId: string; units: models.Unit[]; duration: number }
    | { type: 'UpdateUserCourses'; courseIds: string[] }
    | { type: 'LoadUserCoursesResult'; items: Course[] }
    | { type: 'LoadCourse'; courseId: string }
    | { type: 'LoadCourseResult'; course: Course };

// Course context.
export const CourseContext = createContext<[state: CourseStore, reducer: Dispatch<CourseAction>]>(undefined!);

// Course context provider.
export const CourseContextProvider = injectIntl((props: ContextProps) => {
    const { intl } = props;
    const { visibleCourses, visibleCourseIds, contentBaseUrl } = useConfig();
    const runner = useAsyncRunner();

    // Converts data model to view model.
    const toModel = useCallback(
        (data: models.Course) => {
            return {
                ...data,
                cardImage: isNonEmptyString(data.cardImage)
                    ? isAbsoluteUrl(data.cardImage)
                        ? data.cardImage
                        : getContentUrl(contentBaseUrl, data.cardImage)
                    : getContentUrl(contentBaseUrl, 'assets/course-cards/default.png'),
            } as Course;
        },
        [contentBaseUrl]
    );

    // Populate courses on load.
    useAsyncEffect(
        // eslint-disable-next-line react-hooks/exhaustive-deps
        async () =>
            runner.run({
                task: async () => {
                    const data = await anonymousClient.listCourses();

                    // Filter courses based on config settings.
                    const filtered = !visibleCourses.length
                        ? data
                        : data.filter(({ id }) => visibleCourses.includes(id));

                    if (isNonEmptyString(visibleCourseIds)) {
                        const ids = visibleCourseIds.split(',').map((o) => o.trim());
                        await Promise.allSettled(
                            ids.map(async (id) => {
                                try {
                                    filtered.push(await anonymousClient.getCourse(id));
                                } catch (e) {
                                    trackTrace(`Unable to load course info for ${id}`, SeverityLevel.Error);
                                    trackError(e);
                                }
                            })
                        );
                    }

                    dispatch({ type: 'LoadResult', items: filtered.map(toModel) });
                },
                onError: () => {
                    dispatch({ type: 'LoadError' });
                },
                errorMessage: intl.formatMessage({
                    description: 'Error message for an async operation.',
                    defaultMessage: 'An error occurred while loading list of available courses.',
                }),
            }),
        [toModel, runner, intl, visibleCourses]
    );

    // Loads specified course's units.
    const listCourseUnits = useCallback(
        async (courseId: string) =>
            runner.run({
                task: async () => {
                    const units = await anonymousClient.listCourseUnits(courseId);
                    units.sort((a, b) => a.order - b.order);
                    units.forEach((o) => o.lessons?.sort((a, b) => a.order - b.order));
                    const duration = units.reduce(
                        (pu, u) => pu + (u.lessons?.reduce((pl, l) => pl + (l.duration ?? 0), 0) ?? 0),
                        0
                    );
                    dispatch({ type: 'LoadUnitsResult', courseId, units, duration });
                },
                errorMessage: intl.formatMessage({
                    description: 'Error message for an async operation.',
                    defaultMessage: 'An error occurred while loading course details information.',
                }),
            }),
        [intl, runner]
    );

    // Loads specified courses.
    const loadUserCourses = useCallback(
        async (courseIds: string[]) =>
            runner.run({
                task: async () => {
                    const items = await Promise.all(courseIds.map(async (id) => anonymousClient.getCourse(id)));
                    dispatch({ type: 'LoadUserCoursesResult', items: items.map(toModel) });
                },
                errorMessage: intl.formatMessage({
                    description: 'Error message for an async operation.',
                    defaultMessage: 'An error occurred while loading course details information.',
                }),
            }),
        [intl, runner, toModel]
    );

    // Loads a single course.
    const loadCourse = useCallback(
        async (courseId: string) =>
            runner.run({
                task: async () => {
                    const course = await anonymousClient.getCourse(courseId);
                    dispatch({ type: 'LoadCourseResult', course: toModel(course) });
                },
                errorMessage: intl.formatMessage({
                    description: 'Error message for an async operation.',
                    defaultMessage: 'An error occurred while loading course details information.',
                }),
            }),
        [intl, runner, toModel]
    );

    // Store action reducer - updates store state and kicks off async actions.
    const reducer = useCallback(
        (store: CourseStore, action: CourseAction) =>
            produce(store, (draft) => {
                traceEvent('course-context.action', { type: action.type, state: draft.state });
                switch (action.type) {
                    case 'LoadResult': {
                        draft.items = action.items;
                        draft.defaultCourseIds = draft.items.map((o) => o.id);
                        action.items.forEach(async ({ id }) => listCourseUnits(id));
                        draft.state = 'Loaded';
                        break;
                    }
                    case 'LoadError':
                        draft.state = 'NotLoaded';
                        break;
                    case 'LoadUnitsResult': {
                        if (draft.item?.id === action.courseId) {
                            draft.item.units = action.units;
                            draft.item.duration = action.duration;
                        } else {
                            const item = draft.items.find(({ id }) => id === action.courseId);
                            if (item) {
                                item.units = action.units;
                                item.duration = action.duration;
                            }
                        }
                        break;
                    }
                    case 'UpdateUserCourses': {
                        const courseIds = draft.defaultCourseIds.concat(action.courseIds);
                        draft.items = draft.items.filter((o) => courseIds.includes(o.id));
                        const courses = action.courseIds.filter((o) => !draft.items.some((c) => c.id === o));
                        if (courses.length > 0) {
                            loadUserCourses(courses);
                        }
                        break;
                    }
                    case 'LoadUserCoursesResult': {
                        draft.items = draft.items.concat(action.items);
                        draft.items.filter((o) => !o.units).forEach(async (o) => listCourseUnits(o.id));
                        break;
                    }
                    case 'LoadCourse': {
                        draft.item = draft.items.find((o) => o.id === action.courseId);
                        if (draft.item === undefined) {
                            loadCourse(action.courseId);
                        }
                        break;
                    }
                    case 'LoadCourseResult': {
                        draft.item = action.course;
                        listCourseUnits(action.course.id);
                        break;
                    }
                    default:
                        assert.unreachable(action, 'action');
                }
            }),
        [listCourseUnits, loadCourse, loadUserCourses]
    );

    const [state, dispatch] = useReducer(reducer, { items: [], defaultCourseIds: [], state: 'Loading' });
    return <CourseContext.Provider value={[state, dispatch]}>{props.children}</CourseContext.Provider>;
});

// Selector to get whether courses are currently loading.
export const getIsLoadingCourses = createSelector(
    [(state: CourseStore) => state.state],
    (state) => state === 'Loading'
);

// Selector to get specific course by ID.
export const getCourse = createSelector(
    [(state: CourseStore, _) => state, (_, courseId: string) => courseId], // Pass full state
    (store, courseId) => {
        // Check our main course list first
        const itemsCourse = store.items.find(({ id }) => id === courseId);
        if (itemsCourse) {
            return itemsCourse;
        }

        // Check if it's the currently loaded single course
        return store.item?.id === courseId ? store.item : undefined;
    }
);

// Selector that gets curriculum courses sorted by title.
export const getCurriculumCourses = createSelector([(store: CourseStore) => store.items], (items) =>
    items.filter((o) => o.type === 'course').sort((a, b) => compareLocale(a.title, b.title))
);

// Selector that gets activity courses sorted by title.
export const getActivityCourses = createSelector([(store: CourseStore) => store.items], (items) =>
    items.filter((o) => o.type === 'coding-activity').sort((a, b) => compareLocale(a.title, b.title))
);

// Returns a URL to open a course in VS Code.
export const getOpenCourseUrl = (
    courseId: string | undefined,
    profile: string,
    theme: ProfileTheme,
    userId?: string,
    vscodeVersion?: string
) => {
    if (courseId === undefined) {
        return undefined;
    } else {
        return VSCodeUrl.forCourse(apiEnvironment, courseId, {
            accountId: userId,
            profile: getProfileFileName(profile, theme),
            vscodeVersion,
        });
    }
};

// Returns a URL to open a course in VS Code.
export const useOpenCourseUrl = (courseId?: string) => {
    const [courseStore] = useContext(CourseContext);
    const course = getCourse(courseStore, courseId);

    const [userStore] = useContext(UserContext);
    const userId = getUserId(userStore);

    const { courseProfileName, vscodeVersion } = useConfig();
    const [{ profileTheme }] = useTheme();

    return getOpenCourseUrl(course?.id, course?.profileName ?? courseProfileName, profileTheme, userId, vscodeVersion);
};

// Use specific course.
export const useCourse = (courseId: string) => {
    const [{ item }, dispatch] = useContext(CourseContext);
    useEffect(() => dispatch({ type: 'LoadCourse', courseId }), [courseId, dispatch]);
    return item;
};
